// =====================================================================
// Speed ramps for video clips
// =====================================================================
// A clip's speed zones are stretches of its footage, in seconds into the
// source file, that play faster or slower than normal. Footage outside a
// zone plays at 1x. Every change of speed is eased with a short linear
// ramp centred on the boundary.
//
// The ramp is cut into a few constant-speed steps. The preview sets
// `playbackRate` per step and the export emits one Shotstack clip per
// step, so both play exactly the same footage for the same time.
// =====================================================================

export interface SpeedZone {
  /** Seconds into the source file. */
  start: number;
  end: number;
  speed: number;
}

/** A stretch of source footage played at one constant speed. */
export interface SpeedPiece {
  /** Seconds into the source file. */
  from: number;
  to: number;
  speed: number;
}

export const MIN_CLIP_SPEED = 0.25;
export const MAX_CLIP_SPEED = 8;
export const CLIP_SPEED_PRESETS = [0.25, 0.5, 0.75, 1.5, 2, 3, 4, 6, 8] as const;
/** Shortest zone kept, in source seconds. */
export const MIN_SPEED_ZONE_SECONDS = 0.2;
/**
 * Roughly how long each speed change eases over on screen. The footage it
 * spans grows with the speed, so a jump to 8x eases as visibly as one to 2x.
 */
export const SPEED_RAMP_SECONDS = 0.5;
/** A zone ending here runs to the end of the footage. */
export const SPEED_ZONE_OPEN_END = 1_000_000;
const MAX_RAMP_STEPS = 6;
/** Each ramp step plays for at least this long, so no export clip is shorter than a few frames. */
const MIN_STEP_OUTPUT_SECONDS = 0.1;
const EPS = 1e-6;

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function clampClipSpeed(speed: number): number {
  return round2(clamp(Number.isFinite(speed) ? speed : 1, MIN_CLIP_SPEED, MAX_CLIP_SPEED));
}

/**
 * Sorted, non-overlapping zones with sensible speeds. Zones at 1x and
 * zones too short to notice are dropped; a zone that overlaps the one
 * before it starts where that one ends.
 */
export function normaliseSpeedZones(zones: readonly SpeedZone[] | undefined): SpeedZone[] {
  if (!zones?.length) return [];
  const sorted = zones
    .filter((z) => z && Number.isFinite(z.start) && Number.isFinite(z.end) && Number.isFinite(z.speed))
    .map((z) => ({ start: round2(Math.max(0, z.start)), end: round2(Math.max(0, z.end)), speed: clampClipSpeed(z.speed) }))
    .sort((a, b) => a.start - b.start);
  const out: SpeedZone[] = [];
  for (const zone of sorted) {
    const prev = out[out.length - 1];
    const start = prev ? Math.max(zone.start, prev.end) : zone.start;
    if (zone.end - start < MIN_SPEED_ZONE_SECONDS - EPS) continue;
    if (Math.abs(zone.speed - 1) < 0.01) continue;
    out.push({ start, end: zone.end, speed: zone.speed });
  }
  return out;
}

interface Run {
  from: number;
  to: number;
  speed: number;
}

/**
 * Constant-speed runs over the whole source timeline, 1x outside the zones.
 * Footage does not exist before 0 or after an open end, so those edges never ramp.
 */
function runsFor(zones: SpeedZone[]): Run[] {
  const runs: Run[] = [];
  let cursor = -Infinity;
  const push = (from: number, to: number, speed: number) => {
    if (!(to - from > EPS)) return;
    const last = runs[runs.length - 1];
    if (last && Math.abs(last.speed - speed) < 0.001) last.to = to;
    else runs.push({ from, to, speed });
  };
  for (const zone of zones) {
    const start = zone.start <= 0 ? -Infinity : zone.start;
    const end = zone.end >= SPEED_ZONE_OPEN_END ? Infinity : zone.end;
    push(cursor, start, 1);
    push(start, end, zone.speed);
    cursor = end;
  }
  push(cursor, Infinity, 1);
  return runs;
}

/**
 * Every constant-speed piece of the clip's footage, ramps included, from
 * -Infinity to Infinity. Pieces only depend on the zones, so trimming a
 * clip never changes how its ramps play.
 */
/** Runs plus the half-length of the ramp centred on each boundary between them. */
function profileFor(zones: readonly SpeedZone[] | undefined): { runs: Run[]; halves: number[] } {
  const runs = runsFor(normaliseSpeedZones(zones));
  const halves: number[] = [];
  for (let i = 0; i < runs.length - 1; i++) {
    const a = runs[i];
    const b = runs[i + 1];
    const mean = (a.speed + b.speed) / 2;
    halves.push(Math.min((SPEED_RAMP_SECONDS / 2) * mean, (a.to - a.from) / 4, (b.to - b.from) / 4));
  }
  return { runs, halves };
}

export function speedPieces(zones: readonly SpeedZone[] | undefined): SpeedPiece[] {
  const { runs, halves } = profileFor(zones);
  if (runs.length === 1) return [{ from: -Infinity, to: Infinity, speed: runs[0].speed }];
  const pieces: SpeedPiece[] = [];
  for (let i = 0; i < runs.length; i++) {
    const run = runs[i];
    const head = i > 0 ? halves[i - 1] : 0;
    const tail = i < runs.length - 1 ? halves[i] : 0;
    const bodyFrom = run.from + head;
    const bodyTo = run.to - tail;
    if (bodyTo - bodyFrom > EPS) pieces.push({ from: bodyFrom, to: bodyTo, speed: run.speed });
    if (tail > 0) {
      const next = runs[i + 1];
      const rampFrom = run.to - tail;
      const rampLength = tail * 2;
      const meanSpeed = (run.speed + next.speed) / 2;
      const steps = Math.round(clamp(rampLength / meanSpeed / MIN_STEP_OUTPUT_SECONDS, 1, MAX_RAMP_STEPS));
      for (let s = 0; s < steps; s++) {
        const t = (s + 0.5) / steps;
        pieces.push({
          from: rampFrom + (rampLength * s) / steps,
          to: rampFrom + (rampLength * (s + 1)) / steps,
          speed: run.speed + (next.speed - run.speed) * t,
        });
      }
    }
  }
  return pieces;
}

/** Pieces covering source seconds `start`..`end`. */
export function windowSpeedPieces(zones: readonly SpeedZone[] | undefined, start: number, end: number): SpeedPiece[] {
  const out: SpeedPiece[] = [];
  for (const piece of speedPieces(zones)) {
    const from = Math.max(piece.from, start);
    const to = Math.min(piece.to, end);
    if (to - from > EPS) out.push({ from, to, speed: piece.speed });
  }
  return out;
}

/** Shortest piece, in seconds on screen, that the export emits. About 5 frames. */
export const MIN_EXPORT_PIECE_SECONDS = 0.2;

const screenSeconds = (p: SpeedPiece) => (p.to - p.from) / p.speed;

/** One piece covering both, playing the same footage over the same time on screen. */
function mergePieces(a: SpeedPiece, b: SpeedPiece): SpeedPiece {
  const screen = screenSeconds(a) + screenSeconds(b);
  return { from: a.from, to: b.to, speed: screen > 0 ? (b.to - a.from) / screen : a.speed };
}

/**
 * Fewer, longer pieces for playback and export. Neighbours at nearly the
 * same speed are joined, and anything shorter than `minScreenSeconds` on
 * screen is folded into its shorter neighbour. Every merge keeps the
 * footage and the time on screen, so the clip's length never changes.
 * Each piece becomes its own Shotstack clip, so this keeps renders light.
 */
export function coalesceSpeedPieces(pieces: readonly SpeedPiece[], minScreenSeconds = MIN_EXPORT_PIECE_SECONDS): SpeedPiece[] {
  const out: SpeedPiece[] = [];
  for (const piece of pieces) {
    const last = out[out.length - 1];
    if (last && Math.abs(Math.log(piece.speed / last.speed)) < 0.03) out[out.length - 1] = mergePieces(last, piece);
    else out.push({ ...piece });
  }
  while (out.length > 1) {
    let shortest = -1;
    for (let i = 0; i < out.length; i++) {
      if (screenSeconds(out[i]) < minScreenSeconds && (shortest < 0 || screenSeconds(out[i]) < screenSeconds(out[shortest]))) shortest = i;
    }
    if (shortest < 0) break;
    const prev = out[shortest - 1];
    const next = out[shortest + 1];
    // Fold into the shorter neighbour (another ramp step), so long flat sections keep their exact speed.
    const intoPrev = !next || (prev && screenSeconds(prev) <= screenSeconds(next));
    if (intoPrev) out.splice(shortest - 1, 2, mergePieces(prev, out[shortest]));
    else out.splice(shortest, 2, mergePieces(out[shortest], next));
  }
  return out;
}

export function hasSpeedChange(zones: readonly SpeedZone[] | undefined): boolean {
  return normaliseSpeedZones(zones).length > 0;
}

/** Seconds the footage between `start` and `end` takes to play. */
export function speedOutputSeconds(zones: readonly SpeedZone[] | undefined, start: number, end: number): number {
  if (end <= start) return 0;
  return windowSpeedPieces(zones, start, end).reduce((sum, p) => sum + (p.to - p.from) / p.speed, 0);
}

/** Source second reached after playing `output` seconds from source second `start`. */
export function speedSourceAt(zones: readonly SpeedZone[] | undefined, start: number, output: number): number {
  let remaining = Math.max(0, output);
  for (const piece of windowSpeedPieces(zones, start, Infinity)) {
    const playTime = (piece.to - piece.from) / piece.speed;
    if (remaining <= playTime || piece.to === Infinity) return piece.from + remaining * piece.speed;
    remaining -= playTime;
  }
  return start + remaining;
}

/**
 * Where to start in the source so the footage up to `end` plays for
 * `output` seconds. Used when dragging the left edge of a sped-up clip.
 */
export function speedTrimForOutput(zones: readonly SpeedZone[] | undefined, end: number, output: number): number {
  let lo = 0;
  let hi = Math.max(0, end);
  if (speedOutputSeconds(zones, lo, end) <= output) return lo;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (speedOutputSeconds(zones, mid, end) > output) lo = mid;
    else hi = mid;
  }
  return hi;
}

export interface SpeedCurvePoint {
  /** Seconds from the start of the clip on the timeline. */
  t: number;
  speed: number;
}

/**
 * The smooth speed profile of the footage between source seconds `start`
 * and `end`, in timeline time, for drawing. Ramps are sampled so they read
 * as curves.
 */
export function speedCurve(zones: readonly SpeedZone[] | undefined, start: number, end: number): SpeedCurvePoint[] {
  if (end <= start) return [];
  const { runs, halves } = profileFor(zones);
  const knots: Array<{ x: number; speed: number }> = [];
  runs.forEach((run, i) => {
    const head = i > 0 ? halves[i - 1] : 0;
    const tail = i < runs.length - 1 ? halves[i] : 0;
    knots.push({ x: run.from + head, speed: run.speed }, { x: run.to - tail, speed: run.speed });
  });
  const speedAt = (x: number) => {
    for (let k = 0; k < knots.length - 1; k++) {
      const a = knots[k];
      const b = knots[k + 1];
      if (x > b.x) continue;
      if (!Number.isFinite(a.x) || !Number.isFinite(b.x) || b.x - a.x <= EPS) return b.speed;
      return a.speed + ((b.speed - a.speed) * (x - a.x)) / (b.x - a.x);
    }
    return knots[knots.length - 1].speed;
  };
  const xs = new Set<number>([start, end]);
  for (let k = 0; k < knots.length - 1; k++) {
    const a = knots[k];
    const b = knots[k + 1];
    for (const x of [a.x, b.x]) if (x > start && x < end) xs.add(x);
    if (Math.abs(a.speed - b.speed) > 0.001 && Number.isFinite(a.x) && Number.isFinite(b.x)) {
      for (let s = 1; s < 8; s++) {
        const x = a.x + ((b.x - a.x) * s) / 8;
        if (x > start && x < end) xs.add(x);
      }
    }
  }
  return [...xs]
    .sort((a, b) => a - b)
    .map((x) => ({ t: speedOutputSeconds(zones, start, x), speed: speedAt(x) }));
}

/** Source second and playback speed `local` seconds into a scene made of `pieces`. */
export function speedAtSceneTime(pieces: readonly SpeedPiece[], local: number): { source: number; speed: number } {
  let remaining = Math.max(0, local);
  for (const piece of pieces) {
    const playTime = (piece.to - piece.from) / piece.speed;
    if (remaining < playTime) return { source: piece.from + remaining * piece.speed, speed: piece.speed };
    remaining -= playTime;
  }
  const last = pieces[pieces.length - 1];
  return last ? { source: last.to, speed: last.speed } : { source: 0, speed: 1 };
}
